import {inject} from 'react-ioc'
import {makeAutoObservable, reaction} from 'mobx'
import {ctx} from 'new/ctx'
import {AnalyticsEventService} from 'new/services/analytics/AnalyticsEventService'
import {CookieStorageService} from 'new/services/CookieStorageService'
import {getDarkThemeConfig} from 'themes/DarkTheme'
import {getLightThemeConfig} from 'themes/LightTheme'
import {AnalyticsEventName} from 'types/AnalyticsEventName'
import {CookieStorageKey} from 'types/CookieStorageKey'
import {SidebarTheme} from 'types/SidebarTheme'
import {Theme} from 'types/Theme'

export class ThemeStore {
  #abortController = new AbortController()
  #analyticsEventService = inject(this, AnalyticsEventService)
  // We use the CookieStorage service and not the user preferences
  // as we want to display the theme regardless whether we know the user
  #cookieStorage = inject(this, CookieStorageService)

  systemTheme: Theme = window.matchMedia('(prefers-color-scheme: dark)').matches
    ? Theme.DARK
    : Theme.LIGHT

  // This can not be a computed getter since getAntThemeConfig uses CSS variables
  // So first the DOM needs to be updated and THEN the config can be read
  // see #syncWithDom
  antThemeConfig = this.getAntThemeConfig()

  constructor() {
    ctx.themeStore = this
    makeAutoObservable(this)
    this.#initEffects()
  }

  get userTheme() {
    return this.#cookieStorage.getOrUseDefault(CookieStorageKey.UI_THEME)
  }

  get userSidebarTheme() {
    return this.#cookieStorage.getOrUseDefault(
      CookieStorageKey.UI_SIDEBAR_THEME
    )
  }

  get resolvedTheme() {
    if (this.userTheme === Theme.AUTO) {
      return this.systemTheme
    }

    return this.userTheme
  }

  get resolvedSidebarTheme(): SidebarTheme | typeof Theme.DARK {
    if (this.resolvedTheme === Theme.DARK) {
      return Theme.DARK
    }

    return this.userSidebarTheme
  }

  get isDarkTheme() {
    return this.resolvedTheme === Theme.DARK
  }

  get isMixedSidebarTheme() {
    return this.resolvedSidebarTheme === SidebarTheme.MIXED
  }

  getAntThemeConfig() {
    if (this.isDarkTheme) {
      return getDarkThemeConfig()
    }

    return getLightThemeConfig()
  }

  // Reactive CSS property getter that ensures fresh values on theme changes
  useCSSProperty = (property: string): string => {
    // Accessing these properties ensures this function is reactive to theme changes
    this.resolvedTheme

    const style = window.getComputedStyle(document.documentElement)
    return style.getPropertyValue(property)
  }

  setUserTheme(theme: Theme) {
    this.#cookieStorage.set(CookieStorageKey.UI_THEME, theme)
    this.#analyticsEventService.emit(AnalyticsEventName.THEME_TOGGLE, {
      theme,
    })
  }

  setUserSidebarTheme(sidebarTheme: SidebarTheme) {
    this.#cookieStorage.set(CookieStorageKey.UI_SIDEBAR_THEME, sidebarTheme)
  }

  toggleTheme = () => {
    this.setUserTheme(this.isDarkTheme ? Theme.LIGHT : Theme.DARK)
  }

  #syncWithDom() {
    /**
     * Helper functions `getLightTheme` and `getDarkTheme` collect properties
     * from CSS variables, which rely on "data-theme" attribute of the root element,
     * so we need to set it before calling these functions.
     */
    document.documentElement.setAttribute('data-theme', this.resolvedTheme)
    document.documentElement.setAttribute(
      'data-sidebar-theme',
      this.resolvedSidebarTheme
    )

    // This HAS to be read after the DOM is updated because
    // getAntThemeConfig reads CSS variables that are theme dependent
    this.antThemeConfig = this.getAntThemeConfig()
  }

  #initEffects() {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener(
      'change',
      e => {
        this.systemTheme = e.matches ? Theme.DARK : Theme.LIGHT
      },
      {signal: this.#abortController.signal}
    )

    reaction(
      () => [this.resolvedTheme, this.resolvedSidebarTheme],
      () => this.#syncWithDom(),
      {
        fireImmediately: true,
        signal: this.#abortController.signal,
      }
    )
  }

  dispose() {
    this.#abortController.abort()
  }
}
